/**
 * Map Bounds Calculator
 *
 * Single Responsibility: Calculates map bounds from markers
 */

import { MapMarker, MapBounds } from './types';

/**
 * Map Bounds Calculator Class
 *
 * Single Responsibility: Handles all bounds calculation logic
 */
export class MapBoundsCalculator {
  /**
   * Calculate bounds from an array of markers
   */
  static calculateBounds(markers: MapMarker[]): MapBounds | null {
    if (markers.length === 0) {
      return null;
    }

    let north = markers[0].latitude;
    let south = markers[0].latitude;
    let east = markers[0].longitude;
    let west = markers[0].longitude;

    markers.forEach(marker => {
      if (marker.latitude > north) north = marker.latitude;
      if (marker.latitude < south) south = marker.latitude;
      if (marker.longitude > east) east = marker.longitude;
      if (marker.longitude < west) west = marker.longitude;
    });

    return { north, south, east, west };
  }

  /**
   * Calculate center point from markers
   */
  static calculateCenter(
    markers: MapMarker[]
  ): { lat: number; lng: number } | null {
    if (markers.length === 0) {
      return null;
    }

    if (markers.length === 1) {
      return {
        lat: markers[0].latitude,
        lng: markers[0].longitude,
      };
    }

    const avgLat =
      markers.reduce((sum, marker) => sum + marker.latitude, 0) /
      markers.length;
    const avgLng =
      markers.reduce((sum, marker) => sum + marker.longitude, 0) /
      markers.length;

    return { lat: avgLat, lng: avgLng };
  }

  /**
   * Validate marker coordinates
   */
  static isValidMarker(marker: MapMarker): boolean {
    return (
      typeof marker.latitude === 'number' &&
      typeof marker.longitude === 'number' &&
      !isNaN(marker.latitude) &&
      !isNaN(marker.longitude) &&
      marker.latitude >= -90 &&
      marker.latitude <= 90 &&
      marker.longitude >= -180 &&
      marker.longitude <= 180
    );
  }

  /**
   * Filter valid markers
   */
  static filterValidMarkers(markers: MapMarker[]): MapMarker[] {
    return markers.filter(marker => this.isValidMarker(marker));
  }

  /**
   * Get median longitude (for dense-region calculation)
   */
  private static median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  }

  /**
   * Return markers that represent "where most experiences exist" (closest to median center).
   * Use when map is small/compact so the view focuses on the dense region instead of fitting all (including outliers).
   * @param markers valid markers
   * @param fraction fraction of markers to include (default 0.5 = 50% closest to median for a denser view)
   */
  static getMarkersForDenseRegion(
    markers: MapMarker[],
    fraction = 0.5
  ): MapMarker[] {
    if (markers.length <= 2) return markers;

    const medianLat = this.median(markers.map(m => m.latitude));
    const medianLng = this.median(markers.map(m => m.longitude));

    const withDist = markers.map(m => {
      const dLat = m.latitude - medianLat;
      const dLng = m.longitude - medianLng;
      return { marker: m, distSq: dLat * dLat + dLng * dLng };
    });
    withDist.sort((a, b) => a.distSq - b.distSq);

    const take = Math.max(1, Math.ceil(markers.length * fraction));
    return withDist.slice(0, take).map(({ marker }) => marker);
  }
}
